import React, {useEffect, useState} from 'react';
import BackButton from "../../components/BackButton.tsx";
import {useDocumentTitle, useSessionStorage} from "usehooks-ts";
import TextArea from "../../components/form/TextArea.tsx";
import Button from '../../components/form/Button.tsx';
import AssetPicker from "../../components/ui/AssetPicker.tsx";
import {useLocation, useParams} from "wouter";
import Input from "../../components/form/Input.tsx";
import {getHttpClient, swrFetcher} from "@/utils/api.ts";
import useSWR from "swr";
import {FiPlus, FiTrash} from "react-icons/fi";
import useCustomEventListener from "../../components/hooks/useEventListener.ts";
import RenderPostType from "../../components/ui/RenderPostType.tsx";
import RenderMediaAsset from "../../components/RenderMediaAsset.tsx";
import {AssetResponseItem, GenericAsset, MediaAsset, TweetAsset} from "@/@types/AssetTypes.ts";
import {randomString} from "@/utils/func.ts";
import PostPreview from "./PostPreview.tsx";
import DisplayRelativeTime from "../../components/DisplayRelativeTime.tsx";
import AccountChooser from "../../components/AccountChooser.tsx";
import {Account} from "@/types.ts";
import DateTimePicker from "../../components/DateTimePicker.tsx";
import Drawer from "@/components/Drawer.tsx";
import {closeModal, openDrawer, openModal} from "@/components/dispatch_functions.ts";
import Spinner from "@/components/spinner.tsx";
import Modal from "@/components/Modal.tsx";
import {useHotkeys} from "react-hotkeys-hook";
import RenderPostCaption from "@/components/ui/RenderPostCaption.tsx";
import {BsMagic} from "react-icons/bs";
import {create} from "zustand";
import dayjs from "dayjs";

interface VariationStore {
    previewVariationIndex: number;
    setCurrentIndex: (index: number) => void;
    variations: Pick<AssetResponseItem, 'data' | 'type'>[];
    generating: boolean;
    // setVariations: (variations: AssetResponseItem[]) => void;
    generate: (args: Pick<AssetResponseItem,'data' | 'type'>) => void;
    reset: () => void;
}

const useAIVariations = create<VariationStore>((set)=>{
    return {
        reset:()=>{
            set({
                previewVariationIndex: 0,
                variations: [],
                generating: false,
            })
        },
        previewVariationIndex: 0,
        setCurrentIndex: (index) => set({previewVariationIndex:index}),
        variations: [],
        generating: false,
        generate: async ({data, type})=>{
            set({
                generating:true
            })

            const variations: Pick<AssetResponseItem, 'data' | 'type'>[] = await getHttpClient().post('/ai/generate_variations', {
                // post_id:id,
                post:{
                    data,
                    type,
                },
            }).then(({data})=>{
                return data.variations
            }).catch(()=>{
                return []
            })
            set({
                generating:false,
                variations
            })
        }
    }
})


const pickerConfigForType = {
    "instagram-post": {
        multiple:true,
        type:'image,video',
        max:10,
    },
    "instagram-reel": {
        multiple:true,
        type:'video',
    },
    "instagram-story": {
        multiple:true,
        type:'image,video',
    },
    "facebook-page-post": {
        multiple:true,
        type:'image',
        max:10,
    },
    "facebook-page-story": {
        multiple:true,
        type:'image',
    },
    "facebook-page-reel": {
        multiple:true,
        type:'image',
    },
    "twitter-post":{
        multiple:true,
        type:'image,video',
        max:4,
    }
}

const supportedContentAndMedia = (type: string) => {
    switch (type){
        case "instagram-post":
            return "Supports upto 10 images and videos";
        case "instagram-reel":
            return "Supports multiple videos";
        case "instagram-story":
            return "Supports multiple images or videos";
        case "facebook-page-post":
            return "Supports upto 10 images";
        case "facebook-page-story":
            return "Supports multiple images only";
        case "facebook-page-reel":
            return "Supports multiple videos"
        case "twitter-post":
            return "Supports upto 4 media assets per tweet"
    }

}

function DisplayMessaging({type}){
    return <RenderPostType type={type} />
}

// function FaviconForPostType({type}: {type: string}) {
//     switch (type) {
//         case "facebook":
//             return "/images/facebook.sv"
//     }
// }


function PostsCreateOrUpdate() {
    const params = useParams<{type: string, id: string}>();
    const id = params.id;
    const resetStore = useAIVariations((state)=>state.reset);
    useEffect(() => {
        resetStore()
    }, []);
    const {data, isLoading, mutate} = useSWR<AssetResponseItem>(id ? `/assets/${id}` : null, swrFetcher)
    const [title, setTitle] = useSessionStorage('post-title','')
    const [post, setPost] = useState(null);
    const [type, setType] = useState(params.type)
    // useFavicon(`/images/${type.split("-")[0]}.svg`)

    useDocumentTitle(id ? `${type} - Edit Post` : `${type} - Create a new post`)
    useEffect(() => {
        if(data) {
            const item = (data?.data as GenericAsset | TweetAsset);
            setPost(item.post)
            setTitle(item.title)
            setType(data.type)
            setAccount(data.account_id)
            if(data.post_at) {
                setPostAt(new Date(data.post_at))
            }
        }else{
            setPost(data?.data || (type === "twitter-post" ? [{
                id: randomString(),
            }] : {
                id: randomString(),
                caption:'',
                assets:[],
            }))
        }
    }, [data, setTitle, type]);
    const [saving, setSaving] = useState(false)
    const [, navigate] = useLocation()

    const onSave = ()=>{
        // const hasAssets = post.assets?.length > 0;
        // if(!post.caption && !hasAssets) {
        //     alert('Please add some content or media to the post')
        //     return;
        // }
        setSaving(true)
        const status =  post_at && account ? 'pending' : 'draft';
        if(id){
            getHttpClient().patch(`/assets/${id}`,{
                data: {
                    post,
                    title,
                },
                status,
                type,
                post_at: post_at ? new Date(post_at).getTime() : undefined,
                account_id: account,
            }).then(()=>{
                mutate();
                navigate(`/posts/${status === "draft" ? "" : "upcoming"}`)
                setSaving(false)
            })
        }else {
            getHttpClient().post('/assets', {
                data: {
                    post,
                    title,
                },
                post_at: post_at ? new Date(post_at).getTime() : undefined,
                account_id: account,
                status,
                type,
            }).then(({data}) => {
                if(data.id && status === "draft"){
                    navigate(`/posts/edit/${data.id}`)
                }else{
                    navigate(`/posts/${status === "draft" ? "" : "upcoming"}`)
                }
                setSaving(false)
            })
        }
    }

    // const [keys, { start, stop, isRecording }] = useRecordHotkeys()
    useHotkeys('meta+s', onSave,{
        preventDefault:true,
        enableOnFormTags:true,
    })
    useHotkeys('ctrl+s', onSave, {
        preventDefault:true,
        enableOnFormTags:true,
    })
    const [post_at, setPostAt] = useState(null)
    const [account, setAccount] = useState(null)
    const {data: acct } = useSWR(account ? `/accounts/${account}` : null, swrFetcher)
    const [location, setLocation] = useState(null)
    const {data: prediction} = useSWR(!id || (type && id && !isLoading && !data?.post_at)  ? `/ai/next_post_at?type=${type || ''}` : null, swrFetcher)
    useEffect(() => {
        if(prediction?.predicted
            && dayjs(prediction?.predicted).isAfter(dayjs()) &&
            dayjs(prediction?.predicted).isBefore(dayjs().add(1, 'year'))
        ){
            setPostAt(new Date(prediction?.predicted))
        }
    }, [prediction]);
    const hasChanged = true;
    if(isLoading){
        return <Spinner />
    }
    if(!post) return null;
    return (
        <div>
            <BackButton />
            <div className="flex items-center justify-between">
                <h2 className={'text-2xl mb-2'}>{id ? "Edit Post" : "Create a new post"}</h2>
                {
                    id ?
                   <DeletePost id={id}/> : null
                }
            </div>
            <div className={"mb-2"}>
                <DisplayMessaging type={type} />
                <small className={"opacity-50 block"}>
                    {supportedContentAndMedia(type)}
                </small>
            </div>

            <div className={"grid grid-cols-1 gap-3 md:grid-cols-2"}>
                <div>
                    <Input label={"Post title"} placeholder={"Post title"} selectOnFocus value={title} onChange={setTitle}/>
                    <small className={"opacity-50"}>For your reference only</small>
                    <div className="ml-2">
                        {
                            Array.isArray(post) ? <div className={"flex flex-col gap-2"}>
                                {
                                    post.map((i, index) => {
                                        return <div key={String(i.id)}>
                                            <PostInput key={i.id} value={i} type={type} onChange={(val) => {
                                                setPost(post.map((item) => {
                                                    if (item.id === i.id) {
                                                        return val;
                                                    }
                                                    return item;
                                                }))
                                            }}/>
                                            {
                                                index > 0 ? <div className="text-center">
                                                    <button
                                                        className={"btn btn-link text-danger text-decoration-none"}
                                                        onClick={() => {
                                                            setPost(post.filter((item, i) => index !== i))
                                                        }}>Delete tweet
                                                    </button>
                                                </div> : null
                                            }
                                        </div>
                                    })
                                }
                            </div> : <PostInput value={post}
                                                type={type}
                                                onChange={setPost}/>
                        }

                        {
                            type === "twitter-post" ?
                                <div className="text-center">
                                    <button onClick={() => {
                                        setPost([...post, {
                                            id: randomString()
                                        }])
                                    }} className={"btn btn-link text-decoration-none"}>
                                        <FiPlus/> Add another tweet
                                    </button>
                                </div> : null
                        }
                    </div>
                    <ul className="list-group mb-2 mt-3">
                        {/*{*/}
                        {/*    type === "instagram-post" ?*/}
                        {/*        <li  className="list-group-item py-3 gap-1 space-between flex-wrap">*/}
                        {/*            <label lang={"label"}>Location</label>*/}
                        {/*            <div className="flex-1 max-w-54">*/}
                        {/*                <AsyncSelectInput value={location} onChange={setLocation}*/}
                        {/*                    onSearch={(e)=>{*/}
                        {/*                        // getHttpClient().*/}
                        {/*                        return Promise.resolve([])*/}
                        {/*                    }}*/}
                        {/*                />*/}
                        {/*            </div>*/}
                        {/*        </li>*/}
                        {/*        : null*/}
                        {/*}*/}
                        <li className="list-group-item py-3 gap-1 space-between flex-wrap">
                        <span>
                            Post on
                        </span>
                            <div>
                                <DateTimePicker value={post_at ? post_at : undefined}
                                                placeholder={"Choose when to post"}
                                                onChange={setPostAt}
                                />
                                {
                                    post_at ? <>

                                {
                                    new Date(post_at).getTime() < Date.now() ?
                                        <div className="text-end">
                                            <span className={"badge bg-danger"}>Publish right now</span>
                                        </div>
                                        :
                                        <div className={"text-end"}>
                                            (<DisplayRelativeTime time={post_at} />)
                                        </div>
                                }</> : null
                                }
                            </div>
                        </li>
                        <li className="list-group-item py-3 space-between">
                        <span>
                            Account
                        </span>
                            <span>
                            <AccountChooser
                                    platform={type?.split("-")[0] as Account['platform']}
                                    value={account}
                                    onChange={setAccount}/>
                        </span>
                        </li>
                    </ul>
                    <div className="d-flex mt-2 align-items-center gap-4">
                        {
                            hasChanged ?
                                <Button onClick={onSave} loading={saving} className={"btn btn-primary"}>{
                                    id ? "Save Changes" : "Submit"
                                }</Button> : null
                        }
                    </div>

                </div>
                <PreviewPost
                    account={acct}
                    post_at={post_at}
                    update={(post)=>setPost(post)}
                    type={type} post={post} />
            </div>
        </div>
    );
}

function PreviewPost({type, post, account, post_at, update}:{
    type: string,
    account?: AssetResponseItem['account'],
    post_at?: string,
    update: (post: GenericAsset['post']) => void,
    post:  GenericAsset['post']
}){
    const variations = useAIVariations(state => state.variations)
    const currentIndex = useAIVariations(state => state.previewVariationIndex)
    const setCurrentIndex = useAIVariations(state => state.setCurrentIndex)
    const allPosts: GenericAsset['post'][] = variations ? [post as GenericAsset['post'], ...variations.map(({data})=>(data as GenericAsset).post)] : [post as GenericAsset['post']]
    return   <div className={"bg-base-200 rounded-md"}>
        <div className={"grid grid-cols-3 mt-2 justify-between items-center"}>
            {
                variations?.length > 0 && currentIndex > 0 ?
                    <button
                        onClick={()=> setCurrentIndex(currentIndex - 1)}
                        className={'btn btn-ghost'}>Prev</button> : <span></span>
            }
            <div className={"text-xl text-center"}>Preview</div>
            {
                variations?.length > 0 && currentIndex < allPosts.length - 1?
                    <button className={'btn btn-ghost'}
                    onClick={()=> setCurrentIndex(currentIndex + 1)}
                    >Next</button> : <span></span>
            }
        </div>
        {/*<h3 className={"text-2xl text-center mt-2"}>Preview</h3>*/}
        <div className="border rounded-lg mx-4 my-2 border-base-200 bg-base-100 overflow-hidden">
            <PostPreview type={type}
                         account={account}
                         post_at={post_at}
                         post={allPosts[currentIndex]} />

        </div>
        {
            currentIndex > 0 ? <div className="mt-2 mx-4 mb-2" ><button
                onClick={()=>{
                    update(allPosts[currentIndex])
                    setCurrentIndex(0)
                }}
                className={"btn btn-primary w-full"}>Use this</button></div> : null
        }
    </div>
}

function AICaptionsCarousel({type}){
    const variations = useAIVariations((state)=>state.variations);
    const setCurrentIndex = useAIVariations((state)=>state.setCurrentIndex);
    const currentIndex = useAIVariations((state)=>state.previewVariationIndex);
    return <div className="flex overflow-x-scroll pb-3">
        {
            variations?.map((i, index)=>{
                console.log({type})
                let caption = '';
                if(type === "twitter-post"){
                    caption = (i.data as TweetAsset).post[0].caption;
                }else{
                    caption = (i.data as GenericAsset).post.caption;
                }
                return   <div
                    onClick={()=>setCurrentIndex(index+1)}
                    className="carousel-item cursor-pointer " key={String(index)}>
                    <div
                        className={`text-sm m-1 no-scrollbar rounded-lg border border-base-300 bg-base-100 shadow px-2 py-1 overflow-hidden 
                            ${(currentIndex-1) == index ? 'text-primary' : ''}
                        `} style={{
                        // maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 80px, rgba(0,0,0,0) 100px',
                        width:200, height:100, overflowY:'scroll', overflowX:'hidden'}}>
                        <RenderPostCaption caption={caption} />
                    </div>

                </div>
            })
        }
    </div>
}


interface PostInputType {
    caption: string,
    assets: AssetResponseItem[],
    id: string;
}

function PostInput({value, onChange, type}: {
    value: PostInputType,
    type: string,
    onChange: (value: PostInputType) => void,
}) {

    const availableCharacters = 280 -  (23 * (value?.assets?.length || 0));
    // const remainingCharacters = 280 - availableCharacters;
    const {
        caption,
        assets,
    } = value || {}
    const setCaption = (caption: string) => {
        onChange({
            ...(value || {}),
            caption,
        } as PostInputType)
    }
    const setAssets = (assets) => {
        onChange({
            ...(value || {}),
            assets
        } as PostInputType)
    }
    const handler = ((e) => {
        const {caption} = e
        // if(title) {
        //     setTitle(title)
        // }
        if (caption) {
            setCaption(caption)
        }
    })
    const doGenVariations = ()=>{
        generateVariations({
            type,
            // id:undefined,
            data:{
                title:'',
                post: {
                    id:randomString(6),
                    caption,
                    assets
                }
            }
        })
    }
    const variations = useAIVariations((state)=>state.variations);
    const generating = useAIVariations((state)=>state.generating)
    const generateVariations = useAIVariations(state => state.generate)
    useCustomEventListener<{ title: string, caption: string }>(`apply-variation-${value.id}`, handler)
    return (
        <React.Fragment>
            <div className={"card bg-base-200 overflow-hidden px-4 py-2"}>
                <div>
                    <div className="mb-3">
                        <div className={"space-between"}>
                            <label>Post content</label>
                            <Button
                                loading={generating}
                                onClick={doGenVariations}
                                disabled={!caption}
                                className={"btn btn-link text-decoration-none d-inline-flex gap-2 align-items-center"}><BsMagic /> AI Variations</Button>
                        </div>
                    <TextArea
                        maxLength={type === "twitter-post" ? availableCharacters : undefined}
                        onBlur={()=>{
                            if((!variations || variations?.length === 0) && caption && caption.length > 10) {
                                doGenVariations()
                            }
                        }}
                        placeholder={"Post content"} selectOnFocus rows={5} value={caption} onChange={setCaption} />
                    <AICaptionsCarousel type={type} />
                </div>
                <div>
                    <div className="space-between">
                        <label>Post attachments</label>
                        <PickImages
                            type={type}
                            value={assets} onChange={setAssets} />
                    </div>
                </div>

                {
                    assets ?
                        <div className="flex flex-wrap gap-2">
                            {
                                assets.map((i)=>{
                                    const data = i.data as MediaAsset;
                                    return <a href={data.url} target={"_blank"} key={String(data.url)} className={"rounded  shadow overflow-hidden"}>
                                        <RenderMediaAsset width={120} height={120} item={data} />
                                    </a>
                                })
                            }
                        </div> : null
                }
            </div>

        </div>
    </React.Fragment>
    )
}

function DeletePost({id}){
    const [deleting, setDeleting] = useState(false);
    const [, navigate] = useLocation()
    const del = ()=>{
        setDeleting(true)
        getHttpClient().delete(`/assets/${id}`)
            .then(()=>{
                closeModal('delete-post')
                setDeleting(false)
                history.go(-1)
                 // navigate('/posts',{
                 //     replace:true
                 // })
            })
    }
    return <React.Fragment>
        <Modal
            confirmLoading={deleting}
            okText={"Delete"}
            id={"delete-post"}
            onOk={del}
            title={"Confirm Delete"}
            submitBtnClass={"btn btn-error"}>
            <div>Are you sure you want to delete this post ?</div>
        </Modal>
        <Button
            className={"btn btn-link text-error d-inline-flex gap-2 align-items-center text-decoration-none"}
            onClick={()=>openModal('delete-post')}><FiTrash />Delete</Button>

    </React.Fragment>
}

function PickImages({value, type, onChange}) {
    const pickerConfig = pickerConfigForType[type];
    return <React.Fragment>
        <Button onClick={()=>openDrawer('assets')} className={"btn btn-link text-decoration-none"}>Pick from Assets</Button>
        <Drawer title={"Media Assets"}  id="assets" position={"start"}>
            <AssetPicker value={value}
                         max={pickerConfig?.max}
                         type={pickerConfig?.type}
                         onChange={onChange} />
        </Drawer>
    </React.Fragment>
}



export default PostsCreateOrUpdate;
